import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { InventoryLandingResolverService } from '@inventory/services/inventory-landing-resolver.service';
import { PATHS } from 'src/app/shared/constants/paths';
import { BetterBuysGuardService } from '../better-buys/services/better-buys-guard.service';
import { MasterListResolverService } from '../lists/pages/master-list/services/master-list-resolver.service';
import { RecentlyPurchasedResolverService } from '../lists/pages/recently-purchased/services/recently-purchased-resolver.service';
import { ListDetailManagementResolverService } from '../lists/shared/list-detail-management/services/list-detail-management-resolver.service';
import { ProductDetailResolverService } from '@product-detail/services/product-detail-resolver.service';
import { AuthConnectGuardService } from '../shared';
import { FEATURES } from '@shared/constants/splitio-features';
import { CustomerSwitchGuardService } from '@app/shared';
import { SplitGuardService } from '@shared/services/authentication/feature-flag-guard.service';
import { InventoryGuardService } from '@shared/services/authentication/inventory-guard.service';
import { ServiceRequestGuardService } from '@shared/services/authentication/service-request-guard.service';
import { TouchPage } from './touch.page';
import { BetterBuysResolverService } from '../better-buys/services/better-buys-resolver.service';
import { superUserGuard } from '@shared/guards/super-user.guard';
import { guestUserGuard } from '@shared/services/authentication/guest-user-guard.service';
import { SupportCenterResolverService } from '@app/support-center/services/support-center-resolver.service';
import { vptUserGuard } from '@shared/services/authentication/vpt-user-guard';
import { MppGuardService } from '@shared/services/authentication/mpp-guard.service';
import { MppResolverService } from '@app/mpp/service/mpp-resolver.service';
import { newSearchRedirectGuard } from '@shared/guards/new-search-redirect.guard';
import { ListsResolverService } from '@app/lists/resolvers/lists-resolver.service';
import { ListUpdatesResolverService } from '@app/lists/resolvers/list-updates-resolver.service';
import { AiListBuilderResolverService } from '@app/lists/resolvers/ai-list-builder-resolver.service';
import { RebatesResolverService } from '@app/rebates/services/rebates-resolver.service';
import { serviceLevelGuard } from '@app/customer-reporting/guards/service-level.guard';
import { businessAnalyticsGuard } from '@app/customer-reporting/guards/business-analytics.guard';

const routes: Routes = [
  {
    path: '',
    component: TouchPage,
    canActivate: [vptUserGuard],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        redirectTo: PATHS.HOME,
        pathMatch: 'full',
      },
      {
        path: PATHS.HOME,
        loadChildren: () =>
          import('../home/home.module').then(m => m.HomePageModule),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.B2C,
        loadChildren: () =>
          import('../oauth/oauth.module').then(m => m.OAuthModule),
        canActivate: [],
      },
      {
        path: PATHS.BETTER_BUYS,
        loadChildren: () =>
          import('../better-buys/better-buys.module').then(
            m => m.BetterBuysPageModule,
          ),
        canActivate: [AuthConnectGuardService, BetterBuysGuardService],
        resolve: {
          productNumbers: BetterBuysResolverService,
        },
      },
      {
        path: PATHS.DEALS,
        loadChildren: () =>
          import('../deals/deals.module').then(m => m.DealsPageModule),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.DYF,
        loadChildren: () =>
          import('../did-you-forget/did-you-forget.module').then(
            m => m.DidYouForgetPageModule,
          ),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.INVENTORY,
        loadChildren: () =>
          import('@inventory/inventory.module').then(
            m => m.InventoryPageModule,
          ),
        canActivate: [AuthConnectGuardService, InventoryGuardService],
        resolve: {
          inventory: InventoryLandingResolverService,
        },
      },
      {
        path: PATHS.CUSTOMER_REPORTING,
        loadChildren: () =>
          import('../customer-reporting/customer-reporting.module').then(
            m => m.CustomerReportingPageModule,
          ),
        data: SplitGuardService.routeData(
          [FEATURES.split_dynamic_business_analytics.name],
          PATHS.HOME,
        ),
        canActivate: [
          AuthConnectGuardService,
          businessAnalyticsGuard(),
          SplitGuardService,
          guestUserGuard(),
        ],
      },
      {
        path: PATHS.CUSTOMER_REPORTING_DETAIL_DATA,
        loadChildren: () =>
          import(
            '../customer-reporting/pages/detail-data/detail-data.module'
          ).then(m => m.DetailDataPageModule),
        data: SplitGuardService.routeData(
          [
            FEATURES.split_dynamic_business_analytics.name,
            FEATURES.split_dynamic_business_analytics_detail_report.name,
            FEATURES.split_dynamic_business_analytics_sales_table.name,
          ],
          PATHS.HOME,
        ),
        canActivate: [
          AuthConnectGuardService,
          guestUserGuard(),
          businessAnalyticsGuard(),
          SplitGuardService,
        ],
      },
      {
        path: PATHS.CUSTOMER_REPORTING_PRODUCT_USAGE,
        loadChildren: () =>
          import(
            '../customer-reporting/pages/product-usage/product-usage.module'
          ).then(m => m.ProductUsagePageModule),
        data: SplitGuardService.routeData(
          [
            FEATURES.split_dynamic_business_analytics.name,
            FEATURES.split_dynamic_business_analytics_product_report.name,
            FEATURES.split_dynamic_business_analytics_product_table.name,
          ],
          PATHS.HOME,
        ),
        canActivate: [
          AuthConnectGuardService,
          guestUserGuard(),
          businessAnalyticsGuard(),
          SplitGuardService,
        ],
      },
      {
        path: PATHS.CUSTOMER_REPORTING_INVOICE_HISTORY,
        loadChildren: () =>
          import(
            '../customer-reporting/pages/invoice-history/invoice-history.module'
          ).then(m => m.InvoiceHistoryPageModule),
        data: SplitGuardService.routeData(
          [
            FEATURES.split_dynamic_business_analytics.name,
            FEATURES.split_dynamic_business_analytics_invoice_report.name,
            FEATURES.split_dynamic_business_analytics_invoice_table.name,
          ],
          PATHS.HOME,
        ),
        canActivate: [
          AuthConnectGuardService,
          guestUserGuard(),
          businessAnalyticsGuard(),
          SplitGuardService,
        ],
      },
      {
        path: PATHS.CUSTOMER_REPORTING_SERVICE_LEVEL,
        loadChildren: () =>
          import(
            '../customer-reporting/pages/service-level/service-level.module'
          ).then(m => m.ServiceLevelPageModule),
        data: SplitGuardService.routeData(
          [
            FEATURES.split_dynamic_business_analytics.name,
            FEATURES.split_dynamic_business_analytics_service_level.name,
          ],
          PATHS.HOME,
        ),
        canActivate: [
          AuthConnectGuardService,
          guestUserGuard(),
          businessAnalyticsGuard(),
          serviceLevelGuard(),
          SplitGuardService,
        ],
      },
      {
        path: PATHS.CUSTOMER_REPORTING_CONTRACT_PRODUCT_UTILIZATION,
        loadChildren: () =>
          import(
            '../customer-reporting/pages/contract-product-utilization/contract-product-utilization.module'
          ).then(m => m.ContractProductUtilizationPageModule),
        data: SplitGuardService.routeData(
          [
            FEATURES.split_dynamic_business_analytics.name,
            FEATURES.split_dynamic_business_analytics_contract_product.name,
          ],
          PATHS.HOME,
        ),
        canActivate: [
          AuthConnectGuardService,
          guestUserGuard(),
          businessAnalyticsGuard(),
          SplitGuardService,
        ],
      },
      {
        path: PATHS.AI_LIST,
        loadChildren: () =>
          import('../lists/pages/ai-list/ai-list.module').then(
            m => m.AiListPageModule,
          ),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
        resolve: {
          data: AiListBuilderResolverService,
        },
      },
      {
        path: PATHS.LISTS,
        loadChildren: () =>
          import('../lists/lists.module').then(m => m.ListsPageModule),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
        resolve: {
          data: ListsResolverService,
        },
      },
      {
        path: PATHS.LISTS_SEARCH_PRODUCT_NUMBERS,
        loadChildren: () =>
          import(
            '../lists/pages/search-product-numbers/search-product-numbers.module'
          ).then(m => m.SearchProductNumbersPageModule),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.LIST_DETAIL,
        loadChildren: () =>
          import('../lists/pages/list-detail/list-detail.module').then(
            m => m.ListDetailPageModule,
          ),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
        resolve: {
          list: ListDetailManagementResolverService,
        },
      },
      {
        path: PATHS.LIST_MANAGEMENT,
        loadChildren: () =>
          import('../lists/pages/list-management/list-management.module').then(
            m => m.ListManagementPageModule,
          ),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
        resolve: {
          list: ListDetailManagementResolverService,
        },
      },
      {
        path: PATHS.LIST_RECENTLY_PURCHASED,
        loadChildren: () =>
          import(
            '../lists/pages/recently-purchased/recently-purchased.module'
          ).then(m => m.RecentlyPurchasedPageModule),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
        resolve: {
          data: RecentlyPurchasedResolverService,
        },
      },
      {
        path: PATHS.MANAGE_USERS,
        loadChildren: () =>
          import('../manage-users/manage-users.module').then(
            m => m.ManageUsersPageModule,
          ),
        data: SplitGuardService.routeData(
          [FEATURES.split_global_manage_users.name],
          PATHS.HOME,
        ),
        canActivate: [
          AuthConnectGuardService,
          SplitGuardService,
          guestUserGuard(),
        ],
      },
      {
        path: PATHS.MASTER_LIST,
        loadChildren: () =>
          import('../lists/pages/master-list/master-list.module').then(
            m => m.MasterListPageModule,
          ),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
        resolve: {
          data: MasterListResolverService,
        },
      },
      {
        path: PATHS.MORE,
        loadChildren: () =>
          import('../more/more.module').then(m => m.MorePageModule),
        canActivate: [AuthConnectGuardService],
      },
      {
        path: PATHS.PRODUCTS_DEALS_MENU,
        loadChildren: () =>
          import(
            '../more/menu-pages/products-deals-menu/products-deals-menu.module'
          ).then(m => m.ProductsDealsMenuPageModule),
      },
      {
        path: PATHS.MY_BUSINESS_MENU,
        loadChildren: () =>
          import(
            '../more/menu-pages/my-business-menu/my-business-menu.module'
          ).then(m => m.MyBusinessMenuPageModule),
      },
      {
        path: PATHS.MY_ORDERS_MENU,
        loadChildren: () =>
          import(
            '../more/menu-pages/my-orders-menu/my-orders-menu.module'
          ).then(m => m.MyOrdersMenuPageModule),
        canActivate: [guestUserGuard()],
      },
      {
        path: PATHS.NOTIFICATIONS,
        loadChildren: () =>
          import('../notifications/notifications.module').then(
            m => m.NotificationsPageModule,
          ),
        canActivate: [AuthConnectGuardService],
      },
      {
        path: PATHS.ORDER,
        loadChildren: () =>
          import('../order/order.module').then(m => m.OrderPageModule),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.MY_ORDERS,
        loadChildren: () =>
          import('../my-orders/pages/order-status/order-status.module').then(
            m => m.OrderStatusPageModule,
          ),
        canActivate: [
          AuthConnectGuardService,
          superUserGuard(PATHS.MY_ORDERS_VIEW),
          guestUserGuard(),
        ],
      },
      {
        path: PATHS.MY_ORDERS_VIEW,
        loadChildren: () =>
          import(
            '../my-orders/pages/my-orders-view/my-orders-view.module'
          ).then(m => m.MyOrdersViewPageModule),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.PAYMENTS,
        loadChildren: () =>
          import('../payments/payments.module').then(m => m.PaymentsPageModule),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.PRODUCT_DETAIL,
        loadChildren: () =>
          import('../product-detail/product-detail.module').then(
            m => m.ProductDetailPageModule,
          ),
        canActivate: [AuthConnectGuardService, CustomerSwitchGuardService],
        resolve: {
          selectedProductNumber: ProductDetailResolverService,
        },
      },
      {
        path: PATHS.REVIEW_PRODUCTS,
        loadChildren: () =>
          import('../review-products/review-products.module').then(
            m => m.ReviewProductsModule,
          ),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.SELLER_SHOWCASE_PRODUCTS,
        loadChildren: () =>
          import('../seller-showcase/seller-showcase.module').then(
            m => m.SellerShowcaseModule,
          ),
        data: SplitGuardService.routeData(
          [FEATURES.split_global_seller_showcase_products.name],
          PATHS.HOME,
        ),
        canActivate: [
          AuthConnectGuardService,
          SplitGuardService,
          guestUserGuard(),
        ],
      },
      {
        path: PATHS.PROFILE,
        loadChildren: () =>
          import('../profile/profile.module').then(m => m.ProfilePageModule),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.REQUEST_CREDIT,
        loadChildren: () =>
          import(
            '../service-request/pages/request-credit/request-credit.module'
          ).then(m => m.RequestCreditPageModule),
        canActivate: [
          AuthConnectGuardService,
          ServiceRequestGuardService,
          guestUserGuard(),
        ],
      },
      {
        path: PATHS.LIST_UPDATES,
        loadChildren: () =>
          import('../lists/pages/list-updates/list-updates.module').then(
            m => m.ListUpdatesPageModule,
          ),
        resolve: {
          productNumbers: ListUpdatesResolverService,
        },
        canActivate: [AuthConnectGuardService, guestUserGuard()],
      },
      {
        path: PATHS.REQUEST_CREDIT_CONFIRMATION,
        loadChildren: () =>
          import(
            '../service-request/pages/request-credit-confirmation/request-credit-confirmation.module'
          ).then(m => m.RequestCreditPageModule),
        canActivate: [
          AuthConnectGuardService,
          ServiceRequestGuardService,
          guestUserGuard(),
        ],
      },
      {
        path: PATHS.SCOOP,
        loadChildren: () =>
          import('../scoop/scoop.module').then(m => m.ScoopModule),
        canActivate: [AuthConnectGuardService],
      },
      {
        path: PATHS.SEARCH,
        loadChildren: () =>
          import('../search/search.module').then(m => m.SearchPageModule),
        canActivate: [AuthConnectGuardService],
        runGuardsAndResolvers: 'always',
      },
      {
        path: PATHS.NEW_SEARCH,
        loadChildren: () =>
          import('../new-search/new-search.module').then(
            m => m.NewSearchModule,
          ),
        canActivate: [AuthConnectGuardService, newSearchRedirectGuard],
        runGuardsAndResolvers: 'always',
      },
      {
        path: PATHS.NEW_SEARCH_SUBSTITUTES,
        loadChildren: () =>
          import('../new-search/pages/substitutes/substitutes.module').then(
            m => m.SubstitutesPageModule,
          ),
        canActivate: [AuthConnectGuardService],
        runGuardsAndResolvers: 'always',
      },
      {
        path: PATHS.SEARCH_CATALOG,
        loadChildren: () =>
          import('../search/catalog/catalog.module').then(
            m => m.CatalogPageModule,
          ),
        canActivate: [AuthConnectGuardService],
      },
      {
        path: PATHS.SEARCH_BROWSE,
        loadChildren: () =>
          import('../search/browse/browse.module').then(m => m.BrowseModule),
        canActivate: [AuthConnectGuardService],
      },
      {
        path: PATHS.SEARCH_COMPARE,
        loadChildren: () =>
          import('../search/compare/product-compare.module').then(
            m => m.ProductCompareModule,
          ),
        canActivate: [AuthConnectGuardService],
        runGuardsAndResolvers: 'always',
      },
      {
        path: PATHS.NEW_SEARCH_COMPARE,
        loadChildren: () =>
          import('../search/compare/product-compare.module').then(
            m => m.ProductCompareModule,
          ),
        canActivate: [AuthConnectGuardService],
      },
      {
        path: PATHS.SUPPORT_CENTER,
        loadChildren: () =>
          import('../support-center/support-center.module').then(
            m => m.SupportCenterPageModule,
          ),
        canActivate: [AuthConnectGuardService, guestUserGuard()],
        resolve: {
          supportCenterContent: SupportCenterResolverService,
        },
      },
      {
        path: PATHS.NOT_FOUND,
        loadChildren: () =>
          import('../notfound/notfound.module').then(m => m.NotfoundPageModule),
      },
      {
        path: PATHS.ERROR,
        loadChildren: () =>
          import('../error/error.module').then(m => m.ErrorPageModule),
      },
      {
        path: PATHS.MPP,
        loadChildren: () =>
          import('../mpp/mpp.module').then(m => m.MppPageModule),
        canActivate: [
          AuthConnectGuardService,
          guestUserGuard(),
          MppGuardService,
        ],
        resolve: {
          mpp: MppResolverService,
        },
      },
      {
        path: PATHS.REBATES,
        loadChildren: () =>
          import('../rebates/rebates.module').then(m => m.RebatesModule),
        resolve: {
          takeoutOption: RebatesResolverService,
        },
      },
      // FOR FEATURE FLAG DEMO ONLY
      // WE CAN ENABLE/DISABLE ROUTES USING FEATURE FLAGS
      {
        path: PATHS.FEATURE_DEMO,
        loadChildren: () =>
          import('../feature-demo/feature-demo.module').then(
            m => m.FeatureDemoPageModule,
          ),
        // pass a string array of the split.io featues
        // and a path to redirectTo if the features are not enabled
        data: SplitGuardService.routeData(
          [FEATURES.split_global_microsoft_login.name],
          PATHS.HOME,
        ),
        canActivate: [
          AuthConnectGuardService,
          SplitGuardService,
          guestUserGuard(),
        ],
      },

      // END OF FEATURE FLAG DEMO ROUTES
      {
        path: '**',
        redirectTo: PATHS.NOT_FOUND,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class TouchPageRoutingModule {}
